import styled from '@emotion/styled';
import { Divider, Space, Table, TableColumnsType, Tooltip } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { FiRefreshCcw } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { BondEntity } from '../../../../../entities/bond.entity';
import { DetailBondEntity } from '../../../../../entities/detailBond.entity';
import { DetenteurEntity } from '../../../../../entities/detenteur.entity';
import { PRIMARY, YELLOW } from '../../../../../shared/colors';
import { formatNum } from '../../../../../shared/constants';
import { getUserPortefeuille, getUserPortefeuilleKPI } from '../network';

const Container = styled.div`
  padding: 0px 5px 5px 5px;
  color: white;

  max-height: calc(100vh - 250px);
  overflow-x: auto;
  overflow-y: auto;

  tr.ant-table-expanded-row > td,
  tr.ant-table-expanded-row:hover > td {
    background-color: #757575;
  }
  width: calc(100vh + 55px);

  &::-webkit-scrollbar {
    width: 0.5vw;
  }

  &::-webkit-scrollbar-thumb {
    background-image: linear-gradient(to bottom, #eceff1, #eceff1);
    border-radius: 10px;
  }

  p {
    margin: 0px;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  > .status {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 40%;

    .profit {
      padding: 5px;
      padding-left: 10px;
      border-radius: 10px;
      color: #008000;
      background-color: #00ff00;
      // font-family: Poppins-B;
      font-size: 20px;
    }

    .loss {
      padding: 5px;
      padding-left: 10px;
      border-radius: 10px;
      color: red;
      background-color: #e57373;
      // font-family: Poppins-B;
      font-size: 20px;
    }
  }

  > .exige {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 350px;
  }
`;

export const Portfolio = ({
  getBond,
}: {
  getBond: (b: BondEntity, ope: '1' | '2', detUrl: string) => void;
}) => {
  const connectedUser = useSelector((state: any) => state.userReducer).user;
  const [portefeuilleKPI, setPortefeuilleKPI] = useState<any>();
  const [detenteursPF, setDetenteursPF] = useState<DetenteurEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // const [userVentes, setUserVentes] = useState<VenteEntity[]>([]);

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setIsLoading(true);

    await getUserPortefeuille(connectedUser.id).then(({ data }) => {
      setDetenteursPF(data);
    });

    await getUserPortefeuilleKPI(connectedUser.id).then(({ data }) => {
      setPortefeuilleKPI(data);
    });
    setIsLoading(false);
  };

  return (
    <Container>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingRight: 10,
        }}
      >
        <div style={{ marginRight: 150 }}>
          <PulseLoader size={5} margin={2} color={YELLOW} loading={isLoading} />
        </div>
        <Tooltip title='Rafraichir' placement='leftTop' color={YELLOW}>
          <span style={{ cursor: 'pointer' }} onClick={fetchData}>
            <FiRefreshCcw />
          </span>
        </Tooltip>
      </div>

      <p style={{ marginBottom: 0 }}>Valeur du portefeuille - KPI</p>
      <Divider style={{ backgroundColor: 'white', width: 270, marginTop: 2 }} />
      <Space size={70}>
        <div style={{ display: 'flex' }}>
          <div style={{ marginRight: 15, color: 'gray' }}>
            <p>Valeur du portefeuille :</p>
            <p>Rendement :</p>
            <p>PNL :</p>
          </div>
          <div>
            <p>
              {portefeuilleKPI
                ? formatNum(portefeuilleKPI?.valeurPortefeuille)
                : '--'}{' '}
              XAF
            </p>
            <p>
              {portefeuilleKPI
                ? Math.round(
                    (portefeuilleKPI?.rendement * (1 + 0.001 / 100) +
                      Number.EPSILON) *
                      100,
                  ) / 100
                : '--'}{' '}
              %
            </p>
            <p>
              {portefeuilleKPI ? formatNum(portefeuilleKPI?.pnl) : '--'} XAF
            </p>
          </div>
        </div>

        <div style={{ display: 'flex' }}>
          <div style={{ marginRight: 15, color: 'gray' }}>
            <p>Valeur marché :</p>
            <p>Duration :</p>
            <p></p>
          </div>
          <div>
            <p>
              {portefeuilleKPI
                ? Math.round(
                    (portefeuilleKPI?.valeurMarche * (1 + 0.001 / 100) +
                      Number.EPSILON) *
                      100,
                  ) / 100
                : '--'}{' '}
              %
            </p>

            <p>
              {portefeuilleKPI
                ? Math.round(
                    (portefeuilleKPI?.duration * (1 + 0.001 / 100) +
                      Number.EPSILON) *
                      100,
                  ) / 100
                : '--'}{' '}
              ans
            </p>
            <p>--</p>
          </div>
        </div>
      </Space>

      <div style={{ marginTop: 20 }}>
        <p>Mon Portefeuille</p>
      </div>

      <Divider
        style={{ backgroundColor: 'white', marginBottom: 10, marginTop: 5 }}
      />

      {detenteursPF.length === 0 ? (
        <p style={{ textAlign: 'center' }}>
          Votre portefeuille est présentement vide...
        </p>
      ) : (
        <div>
          <Table
            columns={COLUMNS}
            scroll={{ x: 'max-content' }}
            dataSource={detenteursPF}
            expandable={{
              expandedRowRender: (row) => {
                return (
                  <Table<DetailBondEntity>
                    columns={SUB_COLUMNS}
                    dataSource={(row as any)?.detailBonds}
                    scroll={{ x: 'max-content' }}
                    rowClassName={(row, index) =>
                      index % 2 === 0 ? 'even-row' : 'odd-row'
                    }
                    rowKey='id'
                    size='small'
                    style={{ zoom: 0.9 }}
                    pagination={false}
                    rowSelection={{
                      type: 'radio',
                      onChange: (selectedRowKeys, selectedRows) => {
                        getBond(selectedRows[0].bond, '2', row.url);
                      },
                    }}
                  />
                );
              },
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <FaMinus
                    onClick={(e: any) => onExpand(record, e)}
                    style={{ cursor: 'pointer' }}
                  />
                ) : (
                  <FaPlus
                    onClick={(e: any) => onExpand(record, e)}
                    style={{ cursor: 'pointer' }}
                  />
                ),
            }}
            rowClassName={(row, index) =>
              index % 2 === 0 ? 'even-row' : 'odd-row'
            }
            rowKey='id'
            size='small'
            style={{ zoom: 0.8 }}
            pagination={false}
          />
        </div>
      )}
    </Container>
  );
};

const COLUMNS = [
  {
    title: 'Code',
    dataIndex: 'code_referent',
  },
  {
    title: 'Détenteur',
    render: (cell: any, row: DetenteurEntity) => `${row.nom} ${row.prenom}`,
  },
  {
    title: 'Gestionnaire',
    render: (cell: any, row: any) =>
      `${row.createur.nom} ${row.createur.prenom}`,
  },
  {
    title: 'Qte titre',
    render: (cell: any, row: any) => `${formatNum(row.qteTotal)}`,
  },
  {
    title: 'Montant',
    render: (cell: any, row: any) => `${formatNum(row.montantTotal)}`,
  },
  // {
  //   title: 'P&L',
  //   dataIndex: 'pnl',
  //   render: (cell: any, row: DetenteurEntity) => (
  //     <span style={cell > 0 ? { color: '#008000' } : { color: '#e57373' }}>
  //       {formatNum(cell)}
  //     </span>
  //   ),
  // },
];

const SUB_COLUMNS: TableColumnsType<DetailBondEntity> = [
  {
    title: 'Pays',
    render: (cell: any, row: DetailBondEntity) => row.bond.pays,
  },
  {
    title: 'Code Titre',
    render: (cell: any, row: DetailBondEntity) => row.parentCode,
  },
  {
    title: 'Date acquisition',
    dataIndex: 'dateAcquisition',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Date maturité',
    render: (cell: any, row: DetailBondEntity) =>
      `${moment(row.bond.dateMaturite).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Qte',
    dataIndex: 'qte',
  },
  {
    title: 'Prix',
    dataIndex: 'prixNominale',
    render: (cell: any) => `${formatNum(cell * 1)}`,
  },
  {
    title: 'Montant',
    dataIndex: 'montant',
    render: (cell: any) => `${formatNum(cell * 1)}`,
  },
  {
    title: 'Décote',
    dataIndex: 'decote',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxInteret',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Rendement',
    dataIndex: 'tauxRendement',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Fréquence',
    dataIndex: 'frequence',
  },
  // {
  //   title: 'P&L',
  //   dataIndex: 'pnl',
  //   render: (cell: any, row: DetailBondEntity) => (
  //     <span style={cell > 0 ? { color: '#008000' } : { color: '#e57373' }}>
  //       {formatNum(cell)}
  //     </span>
  //   ),
  // },
];
